<script lang="ts">
  import Header from '../components/layout/Header.svelte';
  import Footer from '../components/layout/Footer.svelte';
  import SearchBar from '../components/ui/SearchBar.svelte';
  import CalculatorCard from '../components/ui/CalculatorCard.svelte';
  import { fuzzySearchCalculators } from '../utils/fuzzySearch';
  import type { CalculatorCard as CalculatorCardType, CalculatorCategory, CalculatorMeta } from '../types/calculator';

  type MetaModule = { default: CalculatorMeta };
  const metaModules = import.meta.glob<MetaModule>('../calculators/*/meta.ts', { eager: true });

  const calculators = (() => {
    const seen = new Set<string>();
    const uniqueCalculators: CalculatorCardType[] = [];

    for (const module of Object.values(metaModules)) {
      const card = module?.default?.card;
      if (!card) continue;

      const key = card.id;
      if (seen.has(key)) {
        console.warn(`[Home] Skipping duplicate calculator card id: ${key}`);
        continue;
      }

      seen.add(key);
      uniqueCalculators.push(card as CalculatorCardType);
    }

    return uniqueCalculators;
  })();

  const CATEGORIES: CalculatorCategory[] = [
    'Savings & Investing',
    'Loans & Debt',
    'Retirement & Tax',
    'Real Estate',
    'Life & Budget',
    'Business',
    'Health & Fitness',
    'Conversion & Math',
    'Physics & Science',
    'Travel & Distance',
    'Weight & Nutrition',
  ];

  let searchQuery = $state('');
  let activeCategory = $state<CalculatorCategory | null>(null);

  const filteredCalculators = $derived(
    fuzzySearchCalculators(searchQuery, calculators).filter(
      (c) => activeCategory === null || c.category === activeCategory
    )
  );
</script>

<div class="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden">
  <Header />

  <main class="flex-1 flex flex-col items-center w-full py-8 px-4 md:px-10">
    <div class="max-w-[1280px] w-full flex flex-col gap-8">
      <!-- Hero Section & Search -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-6 border-b border-gray-200/60">
        <div class="flex flex-col gap-3 max-w-2xl">
          <h1 class="text-[#111418] text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">Calculate Anything, Instantly</h1>
          <p class="text-[#617589] text-base md:text-lg font-normal leading-normal max-w-xl">
            Your one-stop shop for all kinds of calculators. Simple, modern, fast, and intuitive tools for every calculation you need.
          </p>
        </div>
        <SearchBar bind:value={searchQuery} />
      </div>

      <!-- Category Filter -->
      <div class="flex flex-wrap gap-2">
        <button
          onclick={() => (activeCategory = null)}
          class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors {activeCategory === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}"
        >
          All
        </button>
        {#each CATEGORIES as category}
          <button
            onclick={() => (activeCategory = activeCategory === category ? null : category)}
            class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors {activeCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}"
          >
            {category}
          </button>
        {/each}
      </div>

      <!-- Tools Grid -->
      {#if filteredCalculators.length > 0}
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {#each filteredCalculators as calculator (calculator.id)}
            <CalculatorCard {calculator} />
          {/each}
        </div>
      {:else}
        <div class="flex flex-col items-center justify-center py-16 text-center">
          <span class="material-symbols-outlined text-4xl text-[#9aa2ac] mb-3">search_off</span>
          <p class="text-[#617589] text-lg">No calculators match your search.</p>
        </div>
      {/if}
    </div>
  </main>

  <Footer />
</div>
